import type { ReactNode } from 'react';
import { cn } from '../cn';
import s from './wizard.module.css';

/** Two columns: the step on the left, the running summary on the right (INT-01). */
export function WizardLayout({ children, summary }: { children: ReactNode; summary: ReactNode }) {
  return (
    <div className={s.wiz}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.9rem', minWidth: 0 }}>{children}</div>
      {summary}
    </div>
  );
}

export function WizardProgress({
  step,
  total,
  label,
  next,
}: {
  step: number;
  total: number;
  label: string;
  next: string | null;
}) {
  return (
    <div>
      <div className={s.steps} aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length positional bar
          <i key={i} className={cn(i + 1 < step && s.done, i + 1 === step && s.now)} />
        ))}
      </div>
      <div className={s.stepline}>
        <span>
          <b>
            Step {step} of {total}
          </b>{' '}
          · {label}
        </span>
        {next && <span>Next: {next}</span>}
      </div>
    </div>
  );
}

export function QCard({
  title,
  lead,
  label,
  children,
}: {
  title: ReactNode;
  lead?: ReactNode;
  label: string;
  children?: ReactNode;
}) {
  return (
    <section className={s.qcard} aria-label={label}>
      <h2>{title}</h2>
      {lead && <p className={s.lead}>{lead}</p>}
      {children}
    </section>
  );
}

export function Saved({ children }: { children: ReactNode }) {
  return (
    <span className={s.saved} aria-live="polite">
      <span className={s.g} />
      {children}
    </span>
  );
}

export function WizardNav({ back, status, next }: { back: ReactNode; status: ReactNode; next: ReactNode }) {
  return (
    <div className={s.wnav}>
      {back}
      <span className={cn(s.saved)} style={{ justifyContent: 'center' }}>
        {status}
      </span>
      {next}
    </div>
  );
}

export interface SummaryRow {
  key: string;
  label: string;
  value: ReactNode;
  soft?: boolean;
  state: 'done' | 'now' | 'todo';
  onChange?: () => void;
}

/** "Your case so far": every answer visible and changeable without restarting (INT-01, INT-02). */
export function SummaryPanel({
  rows,
  foot,
  title = 'Your case so far',
}: {
  rows: SummaryRow[];
  foot?: ReactNode;
  title?: string;
}) {
  return (
    <aside className={s.summary} aria-label="Your answers so far">
      <div className={s.sh}>
        <h3>{title}</h3>
        <span className={s.sub}>Tap any answer to change it</span>
      </div>
      {rows.map((r, i) => (
        <div key={r.key} className={cn(s.srow, r.state === 'now' && s.now)}>
          <span
            className={cn(s.m, r.state === 'done' && s.done, r.state === 'now' && s.now)}
            aria-hidden="true"
          >
            {r.state === 'done' ? '✓' : i + 1}
          </span>
          <div className={s.b}>
            <div className={s.k}>{r.label}</div>
            <div className={cn(s.v, r.soft && s.soft)}>{r.value}</div>
          </div>
          {r.state === 'done' && r.onChange && (
            <button
              type="button"
              className={s.ed}
              onClick={r.onChange}
              aria-label={`Change ${r.label.toLowerCase()}`}
            >
              Change
            </button>
          )}
        </div>
      ))}
      {foot && <div className={s.sfoot}>{foot}</div>}
    </aside>
  );
}
